import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { userState } from "../../../recoil/auth";
import {
  getCurrentReservations,
  getReservation,
  registerReservation,
  reservationCancel,
  TournamentReservationRequest,
} from "../../../api/reservation";
import { cafeDetail } from "../../../api/cafe";
import { Cafe, ReservationResponse } from "../../../api/types";
import {
  Container,
  TournamentImageWrapper,
  PubContainer,
  ReservationContainer,
  ValueLabel,
  ButtonWrapper,
  Label,
  WarningWrapper,
  TournamentImage,
  ReservationHeader,
} from "./index.styles";
import ReservationStatusPopup from "./ReservationStatusPopup";
import { shareURL } from "../../../utils/common";
import { isApp } from "../../../hooks/useNativeApp";
import ImageViewerModal from "../../../components/common/ImageViewerModal";
import { enqueueSnackbar } from "notistack";
import OpenApp from "./OpenApp";
import useScreenOrientation from "../../../hooks/useScreenOrientation";
import { Reservation } from "./ReservationStatusPopup.types";
import { insetState } from "../../../recoil/inset";
import ReservationPopup from "./components/ReservationPopup";
import ReservationEnd from "./components/ReservationEndPopup";

const ReservationLandingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user] = useRecoilState(userState);
  const history = useHistory();
  const [cafe, setCafe] = useState<Cafe | null>(null);
  const [reservation, setReservation] = useState<ReservationResponse | null>(
    null
  );
  const [openSeedReservation, setOpenSeedReservation] =
    useState<boolean>(false);
  const [isFinalReservation, setIsFinalReservation] = useState(false);
  const [useSeed, setUseSeed] = useState<number>(0);
  const [isReservation, setIsReservation] = useState<boolean>(false);
  const [reservationId, setReservationId] = useState<number>(0);
  const [isStatusPopupOpen, setIsStatusPopupOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isOpenUseApp, setIsOpenUseApp] = useState(false);
  const orientation = useScreenOrientation();
  const [totalReservations, setTotalReservations] = useState(0);
  const [currentList, setCurrentList] = useState<Reservation[]>([]);
  const [type, setType] = useState<"cancel" | "reservation">("reservation");
  const inset = useRecoilValue(insetState);
  useEffect(() => {
    getReservationData();
    getCurrentData();
  }, [id]);

  const getReservationData = async () => {
    if (!id) return;
    getReservation(Number(id)).then((reservation) => {
      setReservation(reservation);
      cafeDetail(reservation.cafeId).then(setCafe);
      const seed = (reservation?.buyIn || 0) / 10000;
      setUseSeed(seed);
    });
  };

  const getCurrentData = async () => {
    if (!id) return;
    getCurrentReservations(+id).then((response) => {
      setTotalReservations(response.length);
      setCurrentList(response);
    });
  };

  useEffect(() => {
    if (!user) return;
    const isReservation = currentList.some(
      (item) => item.nickname === user.nickname
    );
    setIsReservation(isReservation);
    if (isReservation) {
      const reservation = currentList.find((item) => item.userId === user.id);
      if (reservation) {
        setReservationId(+reservation.id);
      }
    }
  }, [currentList, user]);

  useEffect(() => {
    if (!isApp && orientation !== "landscape") {
      setIsOpenUseApp(true);
    }
  }, []);

  const handleClose = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push("/");
    }
  };

  const createReservation = async () => {
    if (!user) return;
    if (!user.validate) {
      enqueueSnackbar("가맹점에 방문하여 인증을 완료해주세요. ");
      return;
    }
    const body: TournamentReservationRequest = {
      reservationId: +id,
      nickname: user.nickname,
      phoneNumber: "",
      userId: user.id,
      route: isApp ? "APP" : "WEB",
    };
    try {
      const response = await registerReservation(body);
      enqueueSnackbar(response.message);
      getReservationData();
      getCurrentData();
    } catch (error: any) {
      enqueueSnackbar(error.message);
    }
  };

  const goToApp = () => {
    if (!isApp) {
      const isKakao = /KAKAOTALK/i.test(navigator.userAgent);
      const redirectUrl =
        window.location.href +
        (window.location.search ? "&" : "?") +
        "store_redirect=true";
      // 앱 실행 시도
      if (isKakao) {
        window.location.href =
          "kakaotalk://web/openExternal?url=" + encodeURIComponent(redirectUrl);
      } else {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isIOS) {
          const link = document.createElement("a");
          link.href = redirectUrl;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          window.open(redirectUrl, "_blank");
          window.close();
        }
      }
      return;
    }
  };

  useEffect(() => {
    // URL에서 store_redirect 파라미터 확인
    const params = new URLSearchParams(window.location.search);
    const shouldRedirect = params.get("store_redirect");

    if (shouldRedirect === "true" && !isApp) {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOS) {
        window.location.href = "https://apps.apple.com/app/6469495456";
      } else {
        window.location.href =
          "https://play.google.com/store/apps/details?id=com.runnersoft.runnerrunner";
      }
    }
  }, []);

  const handleReservationClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!user) {
      enqueueSnackbar("로그인이 필요합니다. ", { variant: "error" });
      history.push("/login");
      return;
    }
    if (reservation?.isSeedPay) {
      setType("reservation");
      setOpenSeedReservation(true);
      return;
    }
    createReservation();
  };

  const handleCancel = async () => {
    if (!user) return;
    if (reservation?.isSeedPay) {
      setType("cancel");
      setOpenSeedReservation(true);
      return;
    }
    try {
      await reservationCancel(reservationId);
      enqueueSnackbar("예약이 취소되었습니다. ");
      getCurrentData();
      getReservationData();
    } catch (e: any) {
      enqueueSnackbar(e.message, { variant: "error" });
    }
  };
  return (
    <Container>
      {reservation && (
        <ReservationContainer isApp={inset}>
          <ReservationHeader isApp={inset}>
            <div className="left">
              <div className="close" onClick={handleClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 19L5 12M5 12L12 5M5 12H19"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="title">
                {cafe?.cafeName ? cafe.cafeName : "예약"}
              </div>
            </div>
            <div
              className="share"
              onClick={() => shareURL(window.location.href, reservation.title)}
            >
              <img
                src="/image-web/reservation/new_share_button.svg"
                alt="To Map"
              />
            </div>
          </ReservationHeader>
          <TournamentImageWrapper>
            <TournamentImage
              alt="토너먼트"
              src={reservation.imageUrl}
              onClick={() => setSelectedImage(reservation.imageUrl)}
            ></TournamentImage>
          </TournamentImageWrapper>
          <div className="title">{reservation.title}</div>
          {cafe && (
            <PubContainer>
              <div className="row">
                <Label>매장이름</Label>
                <ValueLabel>{cafe.cafeName}</ValueLabel>
              </div>
              <div className="row">
                <Label>주소</Label>
                <ValueLabel>{cafe.newAddress}</ValueLabel>
              </div>
              <div className="row">
                <Label>매장번호</Label>
                <ValueLabel>{cafe.vcn}</ValueLabel>
              </div>
            </PubContainer>
          )}
          <WarningWrapper>※미인증 계정은 예약이 불가능합니다.</WarningWrapper>
          <ButtonWrapper>
            <div
              className="state-button"
              onClick={() => setIsStatusPopupOpen(true)}
            >
              <img src="/image-web/reservation/state_button.svg" alt="To Map" />
              <div className="state">{totalReservations}명</div>
            </div>
            <div
              className={`button ${isReservation ? "cancel" : ""}`}
              onClick={(e: any) => {
                if (isReservation) {
                  handleCancel();
                } else {
                  handleReservationClick(e);
                }
              }}
            >
              {isReservation ? "예약취소" : "토너먼트 예약하기"}
            </div>
          </ButtonWrapper>
        </ReservationContainer>
      )}

      <ReservationStatusPopup
        isOpen={isStatusPopupOpen}
        onClose={() => setIsStatusPopupOpen(false)}
        id={id}
        totalReservations={totalReservations}
        currentList={currentList}
      />
      <ImageViewerModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage || ""}
      ></ImageViewerModal>
      {isOpenUseApp && (
        <OpenApp
          openApp={() => goToApp()}
          onClose={() => setIsOpenUseApp(false)}
        />
      )}
      {openSeedReservation && (
        <ReservationPopup
          onClose={() => {
            setOpenSeedReservation(false);
          }}
          reservationId={+id}
          setIsFinalReservation={setIsFinalReservation}
          keepSeed={useSeed}
          type={type}
          tournamentName={reservation?.title || ""}
          id={reservationId}
        />
      )}
      {isFinalReservation && (
        <ReservationEnd
          onClose={() => {
            setIsFinalReservation(false);
            getCurrentData();
          }}
          tournamentTitle={reservation ? reservation.title : ""}
          keepSeed={useSeed}
          type={type}
        />
      )}
    </Container>
  );
};

export default ReservationLandingPage;
